import { useRef, useEffect, useCallback } from 'react';

interface Options {
  delay: number;
}

function getDefaultOptions(): Options {
  return {
    delay: 700,
  };
}

/**
 * ### `useDelayedCallback` Options
 * - `delay` => defaults to 700ms
 */
export function useDelayedCallback<T>(
  callback: (args: T) => void,
  options: Options = getDefaultOptions()
): [(args?: T) => void, () => void] {
  const intervalRef = useRef<number>(0);
  useEffect(() => {
    return () => {
      // clean up
      clearInterval(intervalRef.current);
      intervalRef.current = 0;
    };
  }, []);

  const timeoutCallback = useCallback(
    (args: T) => {
      callback(args);
      clearInterval(intervalRef.current);
      intervalRef.current = 0;
    },
    [callback]
  );

  function callFunction(args?: T) {
    clearInterval(intervalRef.current);
    intervalRef.current = window.setTimeout(
      timeoutCallback,
      options.delay,
      args
    );
  }
  function cancelTimer() {
    clearInterval(intervalRef.current);
    intervalRef.current = 0;
  }
  return [callFunction, cancelTimer];
}
